import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';

const parseMaybeJson = (value) => {
  if (!value || typeof value !== 'string') return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const normalizeServiceRequest = (data) => {
  const serviceRequest = data?.data?.serviceRequest || data;
  if (!serviceRequest) return null;

  return {
    ...serviceRequest,
    schedulingPreferences: parseMaybeJson(serviceRequest.schedulingPreferences),
    additionalDetails: parseMaybeJson(serviceRequest.additionalDetails),
  };
};

const formatDetailValue = (value) => {
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') {
    return Object.entries(value)
      .filter(([, nestedValue]) => Boolean(nestedValue))
      .map(([nestedKey, nestedValue]) => `${nestedKey}: ${formatDetailValue(nestedValue)}`)
      .join(', ');
  }
  return String(value);
};

const AdminServiceRequestDetailPage = () => {
  const { id: requestId } = useParams();
  const navigate = useNavigate();
  const { token, user: currentUser, isLoading: authLoading } = useAuth();
  const [serviceRequest, setServiceRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchServiceRequestDetails = useCallback(async () => {
    if (!token || !requestId) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/service-requests/${requestId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to fetch service request details');
      }
      const data = await response.json();
      setServiceRequest(normalizeServiceRequest(data));
    } catch (err) {
      setError(err.message);
      console.error("Error fetching service request details:", err);
    } finally {
      setIsLoading(false);
    }
  }, [token, requestId]);

  useEffect(() => {
    if (!authLoading && (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'manager'))) {
      navigate('/login');
    } else if (token) {
      fetchServiceRequestDetails();
    }
  }, [authLoading, currentUser, token, navigate, fetchServiceRequestDetails]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  if (authLoading || isLoading) {
    return <div className="container mx-auto p-4 text-center"><p>Loading details...</p></div>;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-center text-red-500"><p>Error: {error}</p></div>;
  }

  if (!serviceRequest) {
    return <div className="container mx-auto p-4 text-center"><p>Service request not found.</p></div>;
  }

  return (
    <div className="container mx-auto p-4 md:p-8">
      <Link 
        to="/admin/service-requests" 
        className="inline-flex items-center text-brand-green-dark hover:text-brand-green-darker mb-6 text-sm font-medium"
      >
        <ArrowLeftIcon className="h-5 w-5 mr-2" />
        Back to All Requests
      </Link>

      <div className="bg-white shadow-xl rounded-lg p-6 md:p-8">
        <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-2xl md:text-3xl font-bold text-brand-text-dark">
            Service Request #{serviceRequest.id}
          </h1>
          <span 
            className={`px-3 py-1 text-sm font-semibold rounded-full leading-tight mt-2 md:mt-0
              ${serviceRequest.status === 'completed' ? 'bg-green-100 text-green-700' : 
                serviceRequest.status === 'cancelled' ? 'bg-red-100 text-red-700' : 
                serviceRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 
                serviceRequest.status === 'confirmed' ? 'bg-blue-100 text-blue-700' : 
                serviceRequest.status === 'in_progress' ? 'bg-indigo-100 text-indigo-700' : 
                'bg-gray-100 text-gray-700'}
            `}
          >
            {(serviceRequest.status || 'pending').charAt(0).toUpperCase() + (serviceRequest.status || 'pending').slice(1).replace('_',' ')}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-brand-green-medium mb-2">Customer Information</h3>
            <p><strong>Name:</strong> {serviceRequest.contactFullName}</p>
            <p><strong>Email:</strong> {serviceRequest.contactEmail}</p>
            <p><strong>Phone:</strong> {serviceRequest.contactPhone}</p>
            <p><strong>Address:</strong> {serviceRequest.contactStreet}, {serviceRequest.contactCity}, {serviceRequest.contactZip}</p>
            {serviceRequest.customer && (
              <p className="mt-1 text-xs text-gray-500">Registered User ID: {serviceRequest.customer.id} ({serviceRequest.customer.username})</p>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-brand-green-medium mb-2">Service Details</h3>
            <p><strong>Service Type:</strong> {serviceRequest.serviceType}</p>
            <p><strong>Description:</strong></p>
            <p className="bg-gray-50 p-3 rounded-md mt-1 whitespace-pre-wrap">{serviceRequest.description}</p>
            <p className="mt-2"><strong>Requested On:</strong> {formatDate(serviceRequest.createdAt)}</p>
            <p><strong>Last Updated:</strong> {formatDate(serviceRequest.updatedAt)}</p>
          </div>
        </div>
        
        {serviceRequest.schedulingPreferences && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-brand-green-medium mb-2">Scheduling Preferences</h3>
            <p><strong>Date 1:</strong> {serviceRequest.schedulingPreferences.preferredDate1} @ {serviceRequest.schedulingPreferences.preferredTime1}</p>
            <p><strong>Date 2:</strong> {serviceRequest.schedulingPreferences.preferredDate2} @ {serviceRequest.schedulingPreferences.preferredTime2}</p>
            {serviceRequest.schedulingPreferences.notes && <p><strong>Notes:</strong> {serviceRequest.schedulingPreferences.notes}</p>}
          </div>
        )}

        {serviceRequest.additionalDetails && typeof serviceRequest.additionalDetails === 'object' && Object.keys(serviceRequest.additionalDetails).length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-brand-green-medium mb-2">Additional Details</h3>
            <ul className="list-disc list-inside pl-1">
              {Object.entries(serviceRequest.additionalDetails).map(([key, value]) => (
                value && <li key={key}><strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {formatDetailValue(value)}</li>
              ))}
            </ul>
          </div>
        )}

        {serviceRequest.technician && (
          <div className="mb-6 pt-4 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-brand-green-medium mb-2">Assigned Technician</h3>
            <p><strong>Name:</strong> {serviceRequest.technician.username}</p>
            <p><strong>Email:</strong> {serviceRequest.technician.email}</p>
          </div>
        )}

        {/* Placeholder for future actions like update status, assign technician, add comments, etc. */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-xl font-semibold text-brand-text-dark mb-4">Manage Request</h3>
          <p className="text-gray-600">Further management options (update status, assign technician, add comments, file attachments) will be available here.</p>
          {/* TODO: Add forms/buttons for updating status, assigning technician, comments, attachments */}
        </div>

      </div>
    </div>
  );
};

export default AdminServiceRequestDetailPage; 